import { useEffect, useState } from 'react';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useMachineSetupStore } from '../stores/machine-setup-store';
import type { OperatorApp } from '@/features/operator-selection/types/operator-types';

export interface LocationEntry {
  actorId: number;
  name: string;
  parentActorId?: number;
}

export interface OperatorLocationPickerState {
  operators: OperatorApp[];
  selectedOperator: OperatorApp | null;
  setSelectedOperator: (op: OperatorApp | null) => void;
  locations: LocationEntry[];
  setLocations: React.Dispatch<React.SetStateAction<LocationEntry[]>>;
  locationsLoading: boolean;
  selectedLocation: LocationEntry | null;
  setSelectedLocation: (loc: LocationEntry | null) => void;
  setOperatorQuery: (q: string) => void;
  setLocationQuery: (q: string) => void;
}

/**
 * Shared hook for operator + location picker logic used in both
 * MachineDetailsStep and ThirdPartyStep. Encapsulates:
 * - Operator list loading
 * - Child location loading when operator changes
 */
export function useOperatorLocationPicker(): OperatorLocationPickerState {
  const [operatorQuery, setOperatorQuery] = useState('');
  const [selectedOperator, setSelectedOperator] = useState<OperatorApp | null>(null);

  const [locationQuery, setLocationQuery] = useState('');
  const [locations, setLocations] = useState<LocationEntry[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationEntry | null>(null);

  const { operators, loadOperators } = useOperatorStore();
  const { loadActorsForOperator } = useMachineSetupStore();

  // Load operator list on mount
  useEffect(() => {
    if (operators.length === 0) loadOperators();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When a new operator is selected, load its child locations
  useEffect(() => {
    if (!selectedOperator) {
      setLocations([]);
      setSelectedLocation(null);
      setLocationQuery('');
      return;
    }
    setLocationsLoading(true);
    setLocations([]);
    setSelectedLocation(null);
    setLocationQuery('');
    loadActorsForOperator(selectedOperator.operatorId)
      .then((actors) => {
        setLocations([...actors].sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => setLocations([]))
      .finally(() => setLocationsLoading(false));
  }, [selectedOperator?.operatorId, loadActorsForOperator]);

  return {
    operators,
    selectedOperator,
    setSelectedOperator,
    locations,
    setLocations,
    locationsLoading,
    selectedLocation,
    setSelectedLocation,
    setOperatorQuery,
    setLocationQuery,
  };
}
